import type { TimeEntryLike } from '@/features/timeEntries/types'

const isCreatedOrFetched = (entry: TimeEntryLike) =>
  entry.status === 'created' || entry.status === 'fetched'

const hasApprovedBy = (entry: TimeEntryLike) =>
  entry.approved_by !== null &&
  entry.approved_by !== undefined &&
  String(entry.approved_by).trim() !== ''

export const isReadyToReleaseTimeEntry = (entry: TimeEntryLike) =>
  entry.timeentry_group?.status !== 'drafted' &&
  isCreatedOrFetched(entry) &&
  (entry.sub_status === 'ready_to_release' || hasApprovedBy(entry))

export const isDraftTimeEntry = (entry: TimeEntryLike) => {
  if (entry.timeentry_group?.status === 'drafted') return true
  if (!isCreatedOrFetched(entry) || isReadyToReleaseTimeEntry(entry)) {
    return false
  }

  return ['', 'draft', 'review_required', 'unassigned'].includes(
    String(entry.sub_status ?? '').toLowerCase()
  )
}

const hasRequiredText = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== ''

const hasAcceptableDraftPayload = (entry: TimeEntryLike) => {
  const group = entry.timeentry_group
  if (typeof group?.can_accept === 'boolean') return group.can_accept

  const validationErrors = group?.accept_validation_errors
  if (
    validationErrors &&
    typeof validationErrors === 'object' &&
    Object.keys(validationErrors).length > 0
  ) {
    return false
  }

  const payload = group?.metadata?.merge_payload ?? entry
  const matterNumber = String(payload.matter_number ?? '')
  return (
    hasRequiredText(payload.client_number) &&
    hasRequiredText(payload.matter_number) &&
    !matterNumber.includes('^') &&
    hasRequiredText(payload.description) &&
    hasRequiredText(payload.start) &&
    hasRequiredText(payload.stop) &&
    Number(payload.duration) > 0
  )
}

export const canAcceptDraftTimeEntry = (entry: TimeEntryLike) =>
  isDraftTimeEntry(entry) && hasAcceptableDraftPayload(entry)

export const canAcceptAllDrafts = (entries: TimeEntryLike[]) => {
  const drafts = entries.filter(isDraftTimeEntry)
  return drafts.length > 0 && drafts.every(canAcceptDraftTimeEntry)
}

export const getDraftReleaseActionState = (entries: TimeEntryLike[]) => ({
  hasDraft: entries.some(isDraftTimeEntry),
  hasReadyToRelease: entries.some(isReadyToReleaseTimeEntry),
})
